// @ts-strict

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ICampaignConfigFieldData, ISingleCampaign, SpecialCampaignData } from '@app/core/models/core-models';
import { JoinCampaignService } from '@app/core/services/join-campaign/join-campaign.service';
import { IJoinCampaignRequestParams } from '@app/core/services/join-campaign/join-campaign.service.types';
import { TrafficSourceManagementService } from '@app/core/services/traffic-sources/traffic-source-management.service';
import { TrafficSourcesService } from '@app/core/services/traffic-sources/traffic-sources.service';
import { ITrafficSourceDomain } from '@app/core/services/traffic-sources/traffic-sources.types';
import { TextareaInputValueEmitter } from '@app/shared/components/modal-inputs/textarea-input/textarea-input.component';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddTrafficSourceModalComponent } from '@app/shared/components/modals/add-traffic-source-modal/add-traffic-source-modal.component';
import { Subject, throwError } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { catchError, finalize } from 'rxjs/operators';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { NotificationTypes } from '@app/core/services/global-notifications/global-notifications.model';
import { TextInputData } from '../../modal-inputs/text-input/text-input.component';
import { GetUserBasicDataService } from '@app/core/services/get-user-basic-data/get-user-basic-data.service';

@Component({
  selector: 'app-verification-modal',
  templateUrl: './verification-modal.component.html',
  styleUrls: ['./verification-modal.component.scss']
})
export class VerificationModalComponent implements OnInit, OnDestroy {
  @Input() currentCampaign?: ISingleCampaign | SpecialCampaignData;
  @Input() couponModal: boolean = false;

  public submitted = false;
  public selectedDomain?: number;
  public field: ICampaignConfigFieldData = {
    system_name: 'promotion_description'
  };
  public field2: ICampaignConfigFieldData = {
    system_name: 'optional_earnings_screenshot'
  };
  public promotionDescription = '';
  public termsAccepted = false;
  public optionalEarningsScreenshot = '';

  public domains: ITrafficSourceDomain[] = [];

  public loading: boolean = false;
  public error: boolean = false;
  public noTrafficSources = false;
  public specialTreatment = false;
  public specialCountries = ['PK', 'IN', 'BD', 'NG'];

  private destroy$ = new Subject<void>();

  get additionalRequirements(): boolean {
    return this.currentCampaign?.additional_requirements || false;
  }

  constructor(
    public activeModal: NgbActiveModal,
    private readonly globalNotificationsService: GlobalNotificationsService,
    private readonly joinCampaign: JoinCampaignService,
    private readonly trafficSourceService: TrafficSourcesService,
    private readonly modalService: NgbModal,
    private readonly trafficSourceManagementService: TrafficSourceManagementService,
    private readonly getUserBasicDataService: GetUserBasicDataService
  ) {}

  ngOnInit(): void {
    this.getTrafficSources();

    this.trafficSourceManagementService.isNewTrafficSourceAdded
      .pipe(
        tap(isAdded => {
          if (isAdded) this.getTrafficSources();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    if (this.additionalRequirements) {
      this.getUserBasicDataService.shareUserData$
        .pipe(
          tap(data => {
            if (this.specialCountries.includes(data?.user?.country_code || '') || this.specialCountries.includes(data?.user?.country_by_ip || '')) {
              this.specialTreatment = true;
            }
          })
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get isCampaignStatusWaiting(): boolean {
    return this.currentCampaign?.status === 'waiting';
  }

  public verify(): void {
    this.submitted = true;

    if (!this.selectedDomain || !this.termsAccepted) {
      this.showNotification('select_traffic_source', NotificationTypes[1]);
      return;
    } else if (this.additionalRequirements && (!this.promotionDescription || this.promotionDescription.length < 100)) {
      this.showNotification('check_if_fields_are_filled_correctly', NotificationTypes[1]);
      return;
    }

    const params: IJoinCampaignRequestParams = {
      traffic_sources: [this.selectedDomain]
    };
    if (this.additionalRequirements) {
      params.description = this.promotionDescription;
    }
    if (this.optionalEarningsScreenshot) {
      params.description_url = this.optionalEarningsScreenshot;
    }

    this.joinCampaign
      .joinCampaign(this.couponModal ? (this.currentCampaign as ISingleCampaign)?.program_id || 0 : this.currentCampaign?.id || 0, params)
      .subscribe(
        res => {
          if (res.status === 'success') {
            this.showNotification('campaign_application_sent_successfully', NotificationTypes[0]);
            this.activeModal.close();
            if (this.currentCampaign) {
              this.currentCampaign.status = 'waiting';
            }
          }
        },
        err => {
          if (err.status_code === 403 && err.errors?.message === 'wait_for_next_apply_24h') {
            this.showNotification(err.errors?.message, NotificationTypes[1]);
          } else {
            this.showNotification('error_occurred_while_sending_campaign_application', NotificationTypes[1]);
            return;
          }
        }
      );
  }

  public getSelectedDomain(domain: number): void {
    this.selectedDomain = domain;
  }

  public getPromotionDescription(text: TextareaInputValueEmitter): void {
    this.promotionDescription = text.value;
  }

  public getOptionalEarningsScreenshot(text: TextInputData): void {
    this.optionalEarningsScreenshot = text.value;
  }

  public openTrafficSourceModal(): void {
    this.modalService.open(AddTrafficSourceModalComponent, { centered: true });
  }

  public getTrafficSources = () => {
    this.loading = true;
    this.error = false;
    this.noTrafficSources = false;

    this.trafficSourceService
      .getAllTrafficSources(true)
      .pipe(
        catchError(err => {
          this.error = true;

          this.noTrafficSources = err.status === 404;

          return throwError(err);
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe(res => {
        this.domains = [];

        Object.keys(res.data?.traffic_sources || {}).forEach(domain => {
          this.domains.push({
            id: Number(domain),
            name: res.data?.traffic_sources?.[Number(domain)] || ''
          });
        });
      });
  };

  private showNotification(message: string, type: (typeof NotificationTypes)[number], translate: boolean = true): void {
    this.globalNotificationsService.show({
      message: translate && !message.includes('view_translations.') ? `view_translations.${message}` : message,
      type,
      translate,
      isPersistent: false,
      id: ''
    });
  }
}
